import { useState, useCallback } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { createApiClient } from '../../services/api';
import { useAppAuth } from '../../hooks/useAppAuth';

interface Props {
  jobId: string;
}

export function DeliveryOtpPanel({ jobId }: Props) {
  const { getToken } = useAppAuth();
  const [otp, setOtp] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOtp = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = createApiClient(getToken);
      const res = await client.get<{ otp: string; expiresInSeconds: number }>(
        `/api/jobs/${jobId}/otp`
      );
      setOtp(res.data.otp);
      setExpiresAt(res.data.expiresInSeconds ? new Date(Date.now() + res.data.expiresInSeconds * 1000) : null);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to generate OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [jobId, getToken]);

  const isExpired = expiresAt ? new Date() > expiresAt : false;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mt-6">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-blue-600" />
        <h2 className="font-semibold text-blue-800">Delivery Verification OTP</h2>
      </div>
      <p className="text-sm text-blue-700 mb-4">
        Share this one-time code with your driver when they arrive to confirm delivery.
        The driver will enter it in their app to complete the handover.
      </p>

      {error && (
        <p className="text-sm text-red-600 mb-3">{error}</p>
      )}

      {otp && !isExpired ? (
        <div className="flex items-center gap-4">
          <div className="bg-white border-2 border-blue-300 rounded-lg px-6 py-3 text-center">
            <p className="text-3xl font-bold font-mono tracking-widest text-blue-900">{otp}</p>
            {expiresAt && (
              <p className="text-xs text-blue-500 mt-1">
                Expires {expiresAt.toLocaleTimeString('en-GB')}
              </p>
            )}
          </div>
          <button
            onClick={fetchOtp}
            disabled={loading}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      ) : (
        <button
          onClick={fetchOtp}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
        >
          {loading ? 'Generating…' : 'Generate OTP'}
        </button>
      )}
    </div>
  );
}
